import { useLocation, useMatches, useNavigation } from 'react-router';
import { EATALY_BREADCRUMB_DEFAULT_BACKGROUND_CLASS, type EatalyBreadcrumbItem } from '@/components/eataly-breadcrumbs';
import type { EatalyBreadcrumbResolvedInput } from '@/components/eataly-breadcrumbs/eataly-breadcrumbs-resolver';
import {
    type EatalyBreadcrumbItemsLoaderValue,
    isThenable,
    toSyncBreadcrumbResult,
} from '@/components/eataly-breadcrumbs/types';
import { usePlpRouteCategory } from '@/hooks/use-plp-route-category';
import { resolveCategoryNodeForPlpPathname } from '@/lib/plp/resolve-category-for-pathname';
import { buildOptimisticPlpBreadcrumbItems } from '@/lib/breadcrumb/build-optimistic-plp-breadcrumb-items';
import { isCategoryPlpPathname } from '@/lib/category-url';
import { useMemo, useRef } from 'react';

/**
 * Subset of leaf route `loader` data that carries pre-built Eataly breadcrumbs. Routes without `breadcrumbItems`
 * are ignored (e.g. cart, account). PLP returns a plain array; PDP returns a deferred bundled result Promise.
 */
export type EatalyBreadcrumbItemsLoaderData = {
    breadcrumbItems: EatalyBreadcrumbItemsLoaderValue | undefined;
    /** Tailwind classes for the nav strip background (e.g. PLP `bg-neutral-white`, PDP `bg-sand-95`). */
    breadcrumbBackgroundClass?: string;
    /** Custom destination for the home icon link. */
    breadcrumbHomeTo?: string;
    /** Whether to show the home icon link. Defaults to true. */
    breadcrumbShowHome?: boolean;
    /** Sync routes only (PLP, content). PDP bundles merge into the deferred `breadcrumbItems` Promise. */
    breadcrumbMergeHomeWithFirstItem?: boolean;
};

export type UseEatalyBreadcrumbsResult = {
    /** Normalized trail + merge flag. PDP keeps a deferred `Promise` for `Await`. */
    items: EatalyBreadcrumbResolvedInput;
    /**
     * Last fully resolved trail. Used as the `Suspense` **fallback** while a deferred PDP
     * `items` Promise resolves, so the strip keeps showing the category and does not go blank.
     */
    staleItemsWhilePending: EatalyBreadcrumbItem[] | undefined;
    /**
     * Background class from the same leaf route as `items`, so the strip matches the current page (e.g. sand on PDP
     * even while the trail fallback still shows the previous PLP items).
     */
    breadcrumbBackgroundClass: string;
    /** Destination for the home icon. */
    homeTo?: string;
    /** Whether to show the home icon. */
    showHome?: boolean;
};

function normalizeLoaderBreadcrumbItems(
    raw: EatalyBreadcrumbItemsLoaderValue | undefined,
    mergeHomeWithFirstItem?: boolean
): EatalyBreadcrumbResolvedInput {
    if (raw == null) {
        return undefined;
    }
    if (isThenable(raw)) {
        return raw;
    }
    return toSyncBreadcrumbResult(raw, mergeHomeWithFirstItem ?? false);
}

/**
 * Returns `breadcrumbItems` from the leaf route loader (`buildEatalyCategoryBreadcrumbItems` /
 * `buildEatalyProductBreadcrumbItems`).
 *
 * While PLP navigation is in flight, rebuilds the trail via `buildOptimisticPlpBreadcrumbItems` (pathname
 * changes between `/c/...` routes). That complements `trimBreadcrumbTrailOnAncestorClick` in the presentational
 * component, which trims immediately on ancestor click (e.g. PDP→category) before the loader finishes.
 *
 * PDP deferred `Promise` items use `staleItemsWhilePending` as the Suspense fallback in `EatalyBreadcrumbsResolver`.
 */
export function useEatalyBreadcrumbs(): UseEatalyBreadcrumbsResult {
    const navigation = useNavigation();
    const location = useLocation();
    const isNavigating = navigation.state === 'loading' || navigation.state === 'submitting';
    const matches = useMatches();
    const routeCategory = usePlpRouteCategory();

    const lastResolvedArray = useRef<EatalyBreadcrumbItem[] | undefined>(undefined);

    const leafBreadcrumb = useMemo(() => {
        const match = [...matches]
            .reverse()
            .find((m) => (m.loaderData as EatalyBreadcrumbItemsLoaderData)?.breadcrumbItems != null);
        if (!match) {
            return {
                items: undefined as EatalyBreadcrumbItemsLoaderValue | undefined,
                mergeHomeWithFirstItem: undefined as boolean | undefined,
                backgroundClass: EATALY_BREADCRUMB_DEFAULT_BACKGROUND_CLASS,
                homeTo: undefined,
                showHome: undefined,
            };
        }
        const data = match.loaderData as EatalyBreadcrumbItemsLoaderData;
        return {
            items: data.breadcrumbItems,
            mergeHomeWithFirstItem: data.breadcrumbMergeHomeWithFirstItem,
            backgroundClass: data.breadcrumbBackgroundClass ?? EATALY_BREADCRUMB_DEFAULT_BACKGROUND_CLASS,
            homeTo: data.breadcrumbHomeTo,
            showHome: data.breadcrumbShowHome,
        };
    }, [matches]);

    const currentItems = leafBreadcrumb.items;
    const currentBackgroundClass = leafBreadcrumb.backgroundClass;

    const optimisticPlpItems = useMemo((): EatalyBreadcrumbItem[] | undefined => {
        if (!isNavigating || !navigation.location || !routeCategory) {
            return undefined;
        }

        const pendingPathname = navigation.location.pathname;
        const pathnameChanged = pendingPathname !== location.pathname;

        if (pathnameChanged) {
            if (!isCategoryPlpPathname(location.pathname) || !isCategoryPlpPathname(pendingPathname)) {
                return undefined;
            }
            const resolvedTarget = resolveCategoryNodeForPlpPathname(routeCategory, pendingPathname);
            return buildOptimisticPlpBreadcrumbItems(routeCategory, {
                currentPathname: location.pathname,
                pendingPathname,
                resolvedTarget,
            });
        }

        return undefined;
    }, [isNavigating, location.pathname, navigation.location, routeCategory]);

    if (Array.isArray(currentItems) && currentItems.length > 0) {
        lastResolvedArray.current = currentItems;
    }

    const base = (items: EatalyBreadcrumbResolvedInput): UseEatalyBreadcrumbsResult => ({
        items,
        staleItemsWhilePending: lastResolvedArray.current,
        breadcrumbBackgroundClass: currentBackgroundClass,
        homeTo: leafBreadcrumb.homeTo,
        showHome: leafBreadcrumb.showHome,
    });

    const normalizedCurrent = normalizeLoaderBreadcrumbItems(currentItems, leafBreadcrumb.mergeHomeWithFirstItem);

    if (isNavigating) {
        if (optimisticPlpItems != null && optimisticPlpItems.length > 0) {
            return base(toSyncBreadcrumbResult(optimisticPlpItems, leafBreadcrumb.mergeHomeWithFirstItem ?? false));
        }
        return base(normalizedCurrent);
    }
    if (currentItems == null) {
        if (navigation.state === 'idle') {
            lastResolvedArray.current = undefined;
        }
        return base(undefined);
    }

    return base(normalizedCurrent);
}
